/**
 * Klaviyo Back-in-Stock Web Component
 *
 * Displays a subscription form when an out-of-stock variant is selected,
 * allowing customers to sign up for back-in-stock notifications via Klaviyo.
 *
 * https://developers.klaviyo.com/en/reference/create_client_back_in_stock_subscription
 * 
 * Listens to: variantAvailabilityChange event on [data-section-type="product"]
 */
class KlaviyoBis extends HTMLElement {
  constructor() {
    super();

    this.currentVariantId = null;
    this.productContainer = null;

    // Bind methods
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleKeypress = this.handleKeypress.bind(this);
  }

  /**
   * Observed attributes for the web component
   */
  static get observedAttributes() {
    return ["variant-id", "company-id"];
  }

  /**
   * Called when the element is added to the DOM
   */
  connectedCallback() {
    this.currentVariantId = this.getAttribute("variant-id");
    this.productContainer = document.querySelector('[data-section-type="product"]');

    this.cacheElements();
    this.bindEvents();

    // Hide by default - will show when out-of-stock variant is selected
    this.hide();
  }

  /**
   * Called when the element is removed from the DOM
   */
  disconnectedCallback() {
    this.unbindEvents();
  }

  /**
   * Called when an observed attribute changes
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "variant-id" && oldValue !== newValue) {
      this.currentVariantId = newValue;
    }
  }

  /**
   * Cache DOM element references
   */
  cacheElements() {
    this.elements = {
      emailInput: this.querySelector("[data-bis-email]"),
      submitButton: this.querySelector("[data-bis-submit]"),
      variantInput: this.querySelector("[data-bis-variant-id]"),
      errorMessage: this.querySelector("[data-bis-error]"),
      successMessage: this.querySelector("[data-bis-success]"),
      formWrapper: this.querySelector("[data-bis-form]"),
    };
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    if (this.productContainer) {
      this.productContainer.addEventListener(
        "variantAvailabilityChange",
        this.handleVariantChange
      );
    }

    if (this.elements.submitButton) {
      this.elements.submitButton.addEventListener("click", this.handleSubmit);
    }

    if (this.elements.emailInput) {
      this.elements.emailInput.addEventListener("keypress", this.handleKeypress);
    }
  }

  /**
   * Unbind event listeners
   */
  unbindEvents() {
    if (this.productContainer) {
      this.productContainer.removeEventListener(
        "variantAvailabilityChange",
        this.handleVariantChange
      );
    }

    if (this.elements.submitButton) {
      this.elements.submitButton.removeEventListener("click", this.handleSubmit);
    }

    if (this.elements.emailInput) {
      this.elements.emailInput.removeEventListener("keypress", this.handleKeypress);
    }
  }

  /**
   * Get the Klaviyo company ID
   */
  get companyId() {
    return this.getAttribute("company-id");
  }

  /**
   * Handle variant availability change event
   * @param {CustomEvent} event
   */
  handleVariantChange(event) {
    const { variant, isOutOfStock } = event.detail;

    if (isOutOfStock && variant) {
      this.currentVariantId = variant.id;
      if (this.elements.variantInput) {
        this.elements.variantInput.value = variant.id;
      }
      this.setAttribute("variant-id", variant.id);
      this.show();
    } else {
      this.hide();
    }
  }

  /**
   * Handle keypress on email input (submit on Enter)
   * @param {KeyboardEvent} event
   */
  handleKeypress(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      this.handleSubmit();
    }
  }

  /**
   * Show the component
   */
  show() {
    this.style.display = "block";
    this.resetForm();
  }

  /**
   * Hide the component
   */
  hide() {
    this.style.display = "none";
    this.resetForm();
  }

  /**
   * Reset the form to its initial state
   */
  resetForm() {
    const { emailInput, errorMessage, successMessage, formWrapper, submitButton } =
      this.elements;

    if (emailInput) emailInput.value = "";

    if (errorMessage) {
      errorMessage.style.display = "none";
      errorMessage.textContent = "";
    }

    if (successMessage) successMessage.style.display = "none";
    if (formWrapper) formWrapper.style.display = "flex";

    if (submitButton) {
      submitButton.disabled = false;
      submitButton.textContent = "Submit";
    }
  }

  /**
   * Show an error message
   * @param {string} message
   */
  showError(message) {
    const { errorMessage, submitButton } = this.elements;

    if (errorMessage) {
      errorMessage.textContent = message;
      errorMessage.style.display = "block";
    }

    if (submitButton) {
      submitButton.disabled = false;
      submitButton.textContent = "Submit";
    }
  }

  /**
   * Show the success message and hide the form
   */
  showSuccess() {
    const { formWrapper, errorMessage, successMessage } = this.elements;

    if (formWrapper) formWrapper.style.display = "none";
    if (errorMessage) errorMessage.style.display = "none";
    if (successMessage) successMessage.style.display = "block";
  }

  /**
   * Set loading state on the submit button
   * @param {boolean} isLoading
   */
  setLoading(isLoading) {
    const { submitButton, errorMessage } = this.elements;

    if (submitButton) {
      submitButton.disabled = isLoading;
      submitButton.textContent = isLoading ? "Submitting..." : "Submit";
    }

    if (isLoading && errorMessage) {
      errorMessage.style.display = "none";
    }
  }

  /**
   * Validate email format
   * @param {string} email
   * @returns {boolean}
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Handle form submission
   */
  async handleSubmit() {
    const email = this.elements.emailInput?.value.trim() || "";

    // Validate email
    if (!email) {
      this.showError("Please enter your email address.");
      return;
    }

    if (!this.isValidEmail(email)) {
      this.showError("Please enter a valid email address.");
      return;
    }

    if (!this.currentVariantId) {
      this.showError(
        "Unable to identify the product variant. Please refresh and try again."
      );
      return;
    }

    this.setLoading(true);

    try {
      await this.submitSubscription(email, this.currentVariantId);
      this.showSuccess();
    } catch (error) {
      this.showError(error.message);
    }
  }

  /**
   * Submit back-in-stock subscription to Klaviyo API
   * @param {string} email
   * @param {string} variantId
   * @returns {Promise<boolean>}
   */
  async submitSubscription(email, variantId) {
    const payload = {
      data: {
        type: "back-in-stock-subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: email,
              },
            },
          },
          channels: ["EMAIL"],
        },
        relationships: {
          variant: {
            data: {
              type: "catalog-variant",
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    };

    const response = await fetch(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.companyId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          revision: "2024-06-15",
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorDetail =
        errorData.errors?.[0]?.detail || "Something went wrong. Please try again.";
      throw new Error(errorDetail);
    }

    return true;
  }
}

// Register the custom element
if (!customElements.get("klaviyo-bis")) {
  customElements.define("klaviyo-bis", KlaviyoBis);
}
